import { defineStore } from 'pinia'
import { shipping, hardware } from '@/data'
import { hardwareByCategory } from '@/utils/hardware'
import { getPlan } from '@/utils/plans'
import { getTaxes } from '@/api/tax'

const DEFAULT_TOTALS: Totals = {
    plan: 0,
    subtotal: 0,
    shipping: 0,
    tax: 0,
    total: 0,
    discount: 0,
    equipment: 0,
    pretax_total: 0,
}

interface CartState {
    hardware: string[];
    plan?: string;
    shipping: string;
    planOptions: StripePlan[];
    couponPromo?: Coupon;
    taxResponse?: TaxPreviewResponse;
    taxLoading: boolean;
    bundle?: Bundle;
}

const shippingById: Record<string, ShippingOption> = shipping.reduce((acc, option) => {
    return {
        ...acc,
        [option.id]: option,
    }
}, {})

const hardwareById: Record<string, Hardware> = hardware.reduce((acc, option) => {
    return {
        ...acc,
        [option.stripe_price_id]: option,
    }
}, {})

export const useCartStore = defineStore('cart', {
    state: (): CartState => ({
        hardware: [],
        plan: undefined,
        shipping: 'standard',
        planOptions: [],
        couponPromo: undefined,
        taxResponse: undefined,
        taxLoading: false,
        bundle: undefined,
    }),
    getters: {
        selectedShippingOption: (state: CartState): ShippingOption | undefined => {
            return state.shipping ? shippingById[state.shipping] : undefined
        },
        selectedPlan: (state: CartState): StripePlan | undefined => {
            return state.plan ? state.planOptions.find((plan) => plan.stripe_price_id === state.plan) : undefined
        },
        hardwareOptionIds(): string[] {
            return this.hardwareOptions.map(option => option.stripe_price_id)
        },
        hardwareOptions(): Hardware[] {
            if (!this.selectedPlan) return this.hardware.map((stripe_price_id) => hardware.find(item => item.stripe_price_id === stripe_price_id) as Hardware)
            // sort hardware options by price, lowest price first
            return this.selectedPlan.hardware_options.sort((a, b) => a.price - b.price)
        },
        hardwareByCategory(): Record<string, Hardware[]> {
            return hardwareByCategory(this.hardwareOptions)
        },
        selectedHardware(): Hardware[] {
            return this.hardware
                .filter((stripe_price_id) => this.hardwareOptions.some((option) => option.stripe_price_id === stripe_price_id))
                .map((stripe_price_id) => this.hardwareOptions.find((option) => option.stripe_price_id === stripe_price_id) as Hardware)
        },
        selectedShippingOptionPrice: (state: CartState): number => shippingById[state.shipping]?.price || 0,
        requireShipping(): boolean {
            return this.selectedHardware.length > 0
        },
        totals(): Totals {
            const totals = { ...DEFAULT_TOTALS }

            if (this.selectedPlan) {
                totals.plan = this.selectedPlan.price
            }
            if (this.bundle?.price) {
                totals.plan = this.bundle.price
            }
            if (!this.bundle && this.selectedHardware.length) {
                totals.equipment = this.selectedHardware.reduce((acc, item) => {
                    if (!item.price) return acc
                    return acc + item.price
                }, 0)
            }
            if (this.shipping) {
                totals.shipping = this.selectedShippingOptionPrice
            }

            if (!this.bundle && this.taxResponse) {
                totals.tax = this.taxResponse.tax_total
                totals.total = this.taxResponse.total_cost
                totals.subtotal = this.taxResponse.sub_total
                if (WR_CHECKOUT_GLOBALS?.flags.isTrialPage) {
                    totals.discount = this.taxResponse.total_cost * -1
                } else {
                    totals.discount = this.taxResponse.total_discount * -1
                }
                totals.pretax_total = (totals.equipment + totals.plan + totals.shipping) - totals.discount
                return totals
            }

            totals.tax = this.taxResponse?.tax_total || totals.tax || 0

            const subtotal = totals.plan + totals.equipment
            const total = subtotal + totals.shipping + totals.tax

            if (WR_CHECKOUT_GLOBALS?.flags.isTrialPage) {
                totals.discount = total * -1
            } else if (this.couponPromo?.amount_off) {
                totals.discount = this.couponPromo.amount_off * -1
            } else if (this.couponPromo?.percent_off) {
                totals.discount = (total * (this.couponPromo?.percent_off / 100)) * -1
            }

            totals.pretax_total = (totals.equipment + totals.plan + totals.shipping) + totals.discount
            totals.subtotal = totals.plan + totals.equipment
            totals.total = total + totals.discount
            return totals
        },
        displayItems(): stripe.paymentRequest.DisplayItem[] {
            const items: stripe.paymentRequest.DisplayItem[] = []
            if (this.selectedPlan) {
                items.push({
                    label: `WIN Membership - ${this.selectedPlan.label}`,
                    amount: this.selectedPlan.price,
                })
            }
            if (this.selectedHardware.length) {
                this.selectedHardware.forEach((hardware) => {
                    items.push({
                        label: hardware.label,
                        amount: hardware.price,
                    })
                })
            }
            if (WR_CHECKOUT_GLOBALS?.flags.isTrialPage) {
                items.push({
                    label: '14 Day Free Trial',
                    amount: this.totals.discount,
                })
            } else if (this.couponPromo?.valid) {
                items.push({
                    label: this.couponPromo?.name || 'Promo Code',
                    amount: this.totals.discount,
                })
            }

            return items
        },
    },
    actions: {
        setPlan(plan: StripePlan) {
            const { stripePlanOptions } = getPlan(plan.group_key, plan.billing_interval)
            this.$patch({
                plan: plan.stripe_price_id,
                planOptions: stripePlanOptions,
            })
        },
        setShipping(shipping: string) {
            this.shipping = shipping
        },
        checkShipping() {
            if (!this.requireShipping) {
                this.shipping = 'standard'
                return
            }
            if (this.selectedHardware.some((hardware) => hardware.category !== 'bat_attachment')) {
                this.shipping = 'standard'
                return
            }
        },
        addHardware(id: string) {
            const hardwareItem = hardwareById[id]
            if (!hardwareItem) return
            if (this.hardware.includes(id)) return
            this.removeHardwareCategory(hardwareItem.category)
            this.hardware.push(id)
        },
        removeHardware(id: string) {
            this.hardware = this.hardware.filter(stripe_price_id => stripe_price_id !== id)
            this.checkShipping()
        },
        removeHardwareCategory(category: string) {
            this.hardware = this.hardware.filter(stripe_price_id => {
                const hardware = hardwareById[stripe_price_id]
                return hardware.category !== category
            })
            this.checkShipping()
        },
        empty() {
            this.$patch({
                hardware: [],
                shipping: undefined,
            })
        },
        productQuantities(): TaxPreviewProductQuantity[] {
            const product_quantities: TaxPreviewProductQuantity[] = []
            const hardwareCoupons = this.couponPromo && this.couponPromo.type === 'promo' ? [this.couponPromo.id] : []
            const subscriptionCoupons = this.couponPromo && this.couponPromo.type === 'membership' ? [this.couponPromo.id] : []

            if (this.selectedPlan && !this.bundle) {
                product_quantities.push({
                    //product_id: this.selectedPlan?.stripe_product_id || undefined,
                    price_id: this.selectedPlan.stripe_price_id,
                    quantity: 1,
                    coupons: subscriptionCoupons,
                })
            }
            if (this.selectedHardware.length) {
                this.selectedHardware.forEach((hardware) => {
                    if (hardware.price !== 0) {
                        if (hardware.coupon) {
                            hardwareCoupons.push(hardware.coupon)
                        }
                        product_quantities.push({
                            //product_id: hardware.stripe_product_id,
                            price_id: hardware.stripe_price_id,
                            quantity: 1,
                            coupons: hardwareCoupons,
                        })
                    }
                })
            }

            if (this.selectedShippingOption && this.selectedShippingOption.price > 0) {
                product_quantities.push({
                    price_id: this.selectedShippingOption.stripe_price_id,
                    quantity: 1,
                    coupons: [],
                })
            }
            // @TODO: bundles
            /*
            if (this.bundle?.plan?.price !== 0 && this.bundle?.plan?.stripe_price_id) {
                product_quantities.push({
                    product_id: this.bundle?.plan?.stripe_product_id,
                    price_id: this.bundle?.plan?.stripe_price_id,
                    quantity: 1,
                    coupons: [],
                })
            }
                if (this.bundle?.equipment?.length) {
                    this.bundle?.equipment?.forEach((equipment) => {
                        if (equipment.price !== 0) {
                            product_quantities.push({
                                product_id: equipment.stripe_product_id,
                                price_id: equipment.stripe_price_id,
                                quantity: 1,
                                coupons: [],
                            })
                        }
                    })
                }
            }
            */

            return product_quantities
        },
        async updateTax(address: AddressFields): Promise<number|undefined> {
            if (!address) return
            try {
                this.taxLoading = true
                const productQuantities = this.productQuantities()
                const coupons = this.couponPromo ? [this.couponPromo.id] : []
                const taxResponse: TaxPreviewResponse = await getTaxes(address, productQuantities, coupons)
                if (!taxResponse) {
                    console.error('no tax response')
                    return
                }
                this.taxResponse = Object.assign({}, taxResponse, {
                    sub_total: taxResponse.sub_total - this.totals.shipping,
                })

                return taxResponse.tax_total
            } catch (error) {
                console.error(error)
            } finally {
                this.taxLoading = false
            }
        },
        handlePlanChange() {
            if (!this.selectedPlan) {
                console.error('no plan selected')
                this.empty()
                return
            }

            this.hardware = []
            // if the plan has a free hardware option, add it to the cart
            if (this.selectedPlan.free_hardware_categories?.length) {
                const freeHardware = this.hardwareOptions.filter((option) => {
                    return this.selectedPlan?.free_hardware_categories?.includes(option.category)
                })
                // loop through the free hardware options and add them to the cart
                freeHardware.forEach((option) => {
                    this.addHardware(option.stripe_price_id)
                })
            } else {
                // if no free hardware, remove all hardware from the cart
                // this.hardware = []
            }

            // if no hardware options, set shipping to standard
            if (!this.hardware.length) {
                this.shipping = 'standard'
            }

            try {
                analytics.track('Plan selected', {
                    plan: this.selectedPlan.group_key,
                    interval: this.selectedPlan.billing_interval,
                    page: 'checkout',
                })
            } catch (e) {
                //
            }
        },
    },
})
